import { memo } from 'react';

import { cn } from '@/shadcn/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/shadcn/select';

import type { DisplayConfig, TableConfigRaw, TableGroup } from '../table.type';

type Props = {
  tableConfig: TableConfigRaw;
  displayConfig: DisplayConfig;
  toggleColumnSelection: (columnKey: string) => void;
  setGroupBy: (group: TableGroup | undefined) => void;
  disableGroupBy?: boolean;
};

export const DisplayMenu = memo(
  ({
    disableGroupBy,
    tableConfig,
    displayConfig,
    toggleColumnSelection,
    setGroupBy
  }: Props) => {
    const selectedColumnsKeys = displayConfig.columns.map(
      colDisplayConfig => colDisplayConfig.key
    );

    const handleChangeGroupBy = (groupByPath: string | undefined) => {
      if (!tableConfig.groups) return;
      const group = tableConfig.groups.find(
        group => group.path === groupByPath
      );
      setGroupBy(group);
    };

    return (
      <div className="flex flex-col gap-3">
        <section className="text-xs">
          <div className="mb-2 font-semibold text-muted-foreground">
            Choose which columns to display
          </div>
          <div className="flex min-w-[200px] flex-wrap gap-[5px]">
            {tableConfig.columns.map(colDef => (
              <button
                className={cn(
                  'm-0 inline-flex h-6 min-w-6 shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-sm border border-border px-2 align-top text-accent-foreground shadow-xs hover:bg-accent',
                  !selectedColumnsKeys.includes(colDef.key) &&
                    'border-transparent bg-transparent shadow-transparent'
                )}
                key={colDef.key}
                onClick={() => toggleColumnSelection(colDef.key)}
                type="button"
              >
                <span>{colDef.header}</span>
              </button>
            ))}
          </div>
        </section>
        {tableConfig.groups && !disableGroupBy && (
          <section>
            <div className="mb-2 font-semibold text-muted-foreground text-xs">
              Group by
            </div>
            <div>
              <Select
                onValueChange={handleChangeGroupBy}
                value={displayConfig.groupBy?.path || 'null'}
              >
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="null">No grouping</SelectItem>
                  {tableConfig.groups.map(group => (
                    <SelectItem key={group.label} value={group.path}>
                      {group.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </section>
        )}
      </div>
    );
  }
);

export default DisplayMenu;
